import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import moment from "moment";

import { GlobalInfo } from "helpers/AppProvider.types";
import { setGlobalInfoSnapshot } from "helpers/globalContext/globalInfoSnapshot";

import { APIHandler } from "utilities/apiHandler";

export interface IAppProviderHandler {
    get(encryptedBuilderId?: string): Promise<GlobalInfo>;
    /**
     * Discriminates this handler's global info from every other handler's in the query cache.
     * Handlers resolving the same global info share a value — every `AppProviderHandler` instance
     * is `"api"`, so the per-layout instances still share one cache entry. A handler whose result
     * depends on an argument must fold that argument in, or two instances collide on one entry.
     */
    cacheKey: string;
}

export class AppProviderHandler implements IAppProviderHandler {
    cacheKey = "api";

    async get(encryptedBuilderId?: string): Promise<GlobalInfo> {
        return await APIHandler(`/api/AccountInfo/GlobalInfo`, {
            method: "GET",
            data: { encryptedBuilderId },
            responseType: GlobalInfo,
        });
    }
}

export const defaultAppProviderHandler = new AppProviderHandler();

// Hooks

/** Queries */

export type BTQueryOptions<
    TQueryFnData = unknown,
    TError = unknown,
    TQueryKey extends unknown[] = [],
    TData = TQueryFnData,
> = Partial<
    Pick<
        UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
        | "onSuccess"
        | "onError"
        | "onSettled"
        | "initialData"
        | "staleTime"
        | "cacheTime"
        | "retry"
        | "retryDelay"
        | "enabled"
        | "refetchOnWindowFocus"
    >
>;

export const globalInfoQueryKey = "globalInfo";
export const useGetGlobalInfoQuery = (
    handler: IAppProviderHandler,
    options?: BTQueryOptions<GlobalInfo, Error, string[]>,
    encryptedBuilderId?: string,
) => {
    return useQuery(
        [globalInfoQueryKey, handler.cacheKey],
        async () => {
            const globalInfo = await handler.get(encryptedBuilderId);
            setGlobalInfoSnapshot(globalInfo);
            return globalInfo;
        },
        {
            retry: 3,
            cacheTime: moment.duration(1, "day").asMilliseconds(),
            staleTime: moment.duration(1, "day").asMilliseconds(),
            ...options,
        },
    );
};
